import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowRight, BookOpen, FileText, Gavel, Send, Quote } from "lucide-react";
import MotionSubscribeBox from "../components/MotionSubscribeBox";

const PRACTICE_LABELS = {
  "criminal-defense": "Federal Criminal Defense",
  "section-1983": "§1983 Civil Rights",
  "appellate": "Federal Appellate",
  "first-amendment": "First Amendment",
  "prosecutor-misconduct": "Prosecutor Misconduct",
  "official-misconduct": "Official Misconduct",
};

export default function Motions() {
  const [current, setCurrent] = useState(null);
  const [archive, setArchive] = useState([]);

  useEffect(() => {
    api.get("/defenders/motions/current").then(r => setCurrent(r.data));
    api.get("/defenders/motions").then(r => setArchive(r.data));
  }, []);

  // Memoize the archive-only list so we don't re-filter on every render
  const archived = useMemo(() => archive.filter(m => !m.is_current), [archive]);

  return (
    <>
      <section className="bg-[#064E3B] text-white" data-testid="motions-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="federal-stamp" style={{ borderColor: "#FACC15", color: "#FACC15", background: "rgba(250,204,21,0.08)" }}>
            ★ MOTION OF THE WEEK · FREE LEGAL EDUCATION
          </div>
          <h1 className="display-black text-5xl lg:text-7xl uppercase mt-6 leading-[0.95] tracking-tighter max-w-5xl">
            One motion.<br />One teardown.<br /><span className="text-[#FACC15]">Every week.</span>
          </h1>
          <p className="text-lg lg:text-xl text-white/85 mt-6 max-w-3xl leading-relaxed">
            We publish the actual text of a great pretrial motion — anonymised — and the Founder breaks down why it works. Free for anyone. Built for lawyers who file, clients who deserve real defence, and students who want to learn what the plea-mill bar won&apos;t teach.
          </p>
          <div className="flex flex-wrap gap-3 mt-10">
            <Link to="/defenders/motions/submit" className="btn-signal" data-testid="motions-submit-cta"><Send className="w-4 h-4" /> Submit Your Motion</Link>
            <Link to="/defenders" className="btn-ghost" style={{ color: "#fff", borderColor: "#fff" }}>Available Defenders</Link>
          </div>
        </div>
      </section>

      {current && (
        <section className="py-16 lg:py-24 border-b border-gray-300" data-testid="motion-current">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">
              // This Week · Filed {current.week_of?.slice(0, 10)}
            </div>
            <h2 className="display-black text-3xl lg:text-5xl uppercase text-[#064E3B] mt-3 tracking-tighter leading-tight max-w-5xl">
              {current.title}
            </h2>

            <div className="mt-6 flex flex-wrap gap-2" data-testid="motion-current-chips">
              <span className="mono text-[10px] tracking-[0.22em] uppercase px-2 py-1 bg-[#064E3B] text-white">{current.case_type}</span>
              <span className="mono text-[10px] tracking-[0.22em] uppercase px-2 py-1 border border-[#D97706] text-[#D97706]">{PRACTICE_LABELS[current.practice_area] || current.practice_area}</span>
              <span className="mono text-[10px] tracking-[0.22em] uppercase px-2 py-1 border border-gray-300 text-gray-700">{current.court}</span>
              <span className="mono text-[10px] tracking-[0.22em] uppercase px-2 py-1 border border-gray-300 text-gray-700">Filed by {current.filed_by}</span>
            </div>

            <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
              <article className="lg:col-span-2">
                <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">// The Motion</div>
                <h3 className="display-black text-xl uppercase text-[#064E3B] mt-2">Preamble</h3>
                <p className="text-base text-gray-800 mt-3 leading-relaxed italic">{current.preamble}</p>

                <h3 className="display-black text-xl uppercase text-[#064E3B] mt-8">Body</h3>
                <pre className="mt-3 text-sm text-gray-900 whitespace-pre-wrap font-sans border-l-4 border-[#064E3B] pl-5 leading-relaxed bg-gray-50 p-6 overflow-x-auto" data-testid="motion-body">
{current.body}
                </pre>
              </article>

              <aside className="space-y-6">
                <div className="border border-[#D97706] bg-red-50 p-6" data-testid="motion-teardown">
                  <div className="flex items-center gap-2">
                    <Quote className="w-5 h-5 text-[#D97706]" />
                    <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#D97706]">// Founder Teardown</div>
                  </div>
                  <h4 className="display-black text-lg uppercase text-[#064E3B] mt-3">Why this works.</h4>
                  <pre className="mt-3 text-sm text-gray-900 whitespace-pre-wrap font-sans leading-relaxed">{current.founder_teardown}</pre>
                  <div className="mono text-[10px] tracking-wider uppercase text-[#D97706] mt-5">— The Defenders Founder</div>
                </div>

                <div className="border border-gray-300 p-6 bg-gray-50">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-[#064E3B]" />
                    <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#064E3B]">// Authorities Cited</div>
                  </div>
                  <ul className="mt-3 space-y-2 text-sm text-gray-900">
                    {current.authorities.map(a => (
                      <li key={a} className="mono text-[12px] text-[#064E3B] flex gap-2">
                        <span className="text-[#D97706]">▸</span> {a}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="border border-[#064E3B] bg-[#064E3B] text-white p-6">
                  <Gavel className="w-5 h-5 text-[#FACC15]" />
                  <div className="display-black text-lg uppercase mt-3">{current.why_it_matters ? "Why It Matters" : "—"}</div>
                  {current.why_it_matters && <p className="text-sm text-white/85 mt-2">{current.why_it_matters}</p>}
                </div>
              </aside>
            </div>
          </div>
        </section>
      )}

      <section className="py-16 lg:py-20" data-testid="motions-archive">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">// The Archive</div>
          <h2 className="display-black text-3xl lg:text-4xl uppercase text-[#064E3B] mt-3">Every motion. Every teardown.</h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-300 border border-gray-300">
            {archived.map(m => (
              <Link key={m.slug} to={`/defenders/motions/${m.slug}`} className="bento-card p-6" data-testid={`motion-archive-${m.slug}`}>
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-gray-500">{m.week_of?.slice(0, 10)} · {m.case_type}</div>
                <h3 className="display-black text-xl uppercase text-[#064E3B] mt-3 leading-tight">{m.title}</h3>
                <p className="text-sm text-gray-700 mt-3 line-clamp-3">{m.why_it_matters}</p>
                <div className="mono text-[11px] tracking-wider uppercase text-[#D97706] mt-4 flex items-center gap-2">
                  Read motion + teardown <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-12 border border-[#064E3B] bg-gray-50 p-7 flex flex-col md:flex-row md:items-center gap-5 justify-between">
            <div>
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#D97706]">// Filed a great motion?</div>
              <div className="display-black text-xl uppercase text-[#064E3B] mt-2">Submit it. Get credit. Build the network.</div>
            </div>
            <Link to="/defenders/motions/submit" className="btn-signal whitespace-nowrap"><FileText className="w-4 h-4" /> Submit Motion</Link>
          </div>

          <div className="mt-10">
            <MotionSubscribeBox variant="dark" />
          </div>
        </div>
      </section>
    </>
  );
}
